import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { Close as CloseIcon, SkipNext as SkipNextIcon } from "@mui/icons-material";

export interface DrawShowTeam {
  id: string;
  name: string;
  flag: string;
  isBye: boolean;
}

interface GroupDrawShowProps {
  title: string;
  plan: { team: DrawShowTeam; groupIdx: number }[];
  groupLetters: string[];
  potCount: number;
  getFlag: (flag: string) => React.ReactNode;
  confirming: boolean;
  onConfirm: () => void;
  onRedraw: () => void;
  onBack: () => void;
  onClose: () => void;
}

const POT_COLORS = ["#FFD54A", "#D5DEE8", "#E39A5B", "#5AB0FF"];
const POT_TAGLINES = ["The favourites", "The contenders", "The challengers", "The underdogs"];

const SPIN_MS = 2800;
const REVEAL_MS = 2200;
const POT_INTRO_MS = 2200;

type Stage = "intro" | "running" | "done";
type Phase = "potIntro" | "spin" | "reveal";

const css = `
@keyframes gd-bg { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
@keyframes gd-pulse { 0%,100% { transform: scale(1); box-shadow: 0 0 24px rgba(255,213,74,.45); } 50% { transform: scale(1.06); box-shadow: 0 0 52px rgba(255,213,74,.9); } }
@keyframes gd-rise { from { opacity: 0; transform: translateY(24px) scale(.96); } to { opacity: 1; transform: none; } }
@keyframes gd-potin { 0% { opacity: 0; transform: scale(2.6); letter-spacing: 30px; filter: blur(14px); } 55% { opacity: 1; transform: scale(1); letter-spacing: 8px; filter: blur(0); } 100% { opacity: 1; transform: scale(1); letter-spacing: 8px; } }
@keyframes gd-ring-pulse { 0%,100% { opacity: .35; } 50% { opacity: 1; } }
@keyframes gd-shake { 0%,100% { transform: translate(0,0) rotate(0); } 25% { transform: translate(-3px,2px) rotate(-.6deg); } 75% { transform: translate(3px,-2px) rotate(.6deg); } }
@keyframes gd-flicker { 0% { opacity: .55; transform: translateY(-8px) scale(.97); } 100% { opacity: 1; transform: none; } }
@keyframes gd-pop { 0% { opacity: 0; transform: scale(.2) rotate(-8deg); } 60% { opacity: 1; transform: scale(1.16) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
@keyframes gd-shock { from { opacity: .9; transform: scale(.2); } to { opacity: 0; transform: scale(3.2); } }
@keyframes gd-spark { from { opacity: 1; transform: rotate(var(--a)) translateY(0) scaleY(1); } to { opacity: 0; transform: rotate(var(--a)) translateY(-210px) scaleY(.2); } }
@keyframes gd-slam { 0% { opacity: 0; transform: scale(1.7); filter: brightness(2.4); } 55% { opacity: 1; transform: scale(.95); } 100% { opacity: 1; transform: scale(1); filter: none; } }
@keyframes gd-glow { 0%,100% { box-shadow: 0 0 0 rgba(255,213,74,0); } 50% { box-shadow: 0 0 34px rgba(255,213,74,.55); } }
@keyframes gd-confetti { 0% { opacity: 1; transform: translateY(-20px) rotate(0deg); } 100% { opacity: 0; transform: translateY(105vh) rotate(720deg); } }
@keyframes gd-title { 0%,100% { text-shadow: 0 0 22px rgba(255,213,74,.35); } 50% { text-shadow: 0 0 48px rgba(255,213,74,.9); } }
`;

const GroupDrawShow: React.FC<GroupDrawShowProps> = ({
  title,
  plan,
  groupLetters,
  potCount,
  getFlag,
  confirming,
  onConfirm,
  onRedraw,
  onBack,
  onClose,
}) => {
  const groups = groupLetters.length;
  const [stage, setStage] = useState<Stage>("intro");
  const [phase, setPhase] = useState<Phase>("potIntro");
  const [idx, setIdx] = useState(0); // number of committed picks
  const [spinTeam, setSpinTeam] = useState<DrawShowTeam | null>(null);

  const current = plan[idx];
  const potIdx = Math.min(Math.floor(idx / groups), potCount - 1);
  const potColor = POT_COLORS[potIdx % POT_COLORS.length];

  const begin = () => {
    setIdx(0);
    setPhase("potIntro");
    setStage("running");
  };

  const skip = () => {
    setIdx(plan.length);
    setStage("done");
  };

  // Phase machine: pot intro -> spin -> reveal -> next pick
  useEffect(() => {
    if (stage !== "running" || !current) return;

    if (phase === "potIntro") {
      const t = window.setTimeout(() => setPhase("spin"), POT_INTRO_MS);
      return () => clearTimeout(t);
    }

    if (phase === "spin") {
      // cycle through every team of the pot, slowing down like a slot machine, landing on the pick
      const pool = plan.slice(potIdx * groups, (potIdx + 1) * groups).map((p) => p.team);
      let elapsed = 0;
      let i = Math.floor(Math.random() * pool.length);
      let t: number;
      const tick = () => {
        if (elapsed >= SPIN_MS) {
          setSpinTeam(current.team);
          setPhase("reveal");
          return;
        }
        i = (i + 1) % pool.length;
        setSpinTeam(pool[i]);
        const delay = 70 + Math.pow(elapsed / SPIN_MS, 2) * 380;
        elapsed += delay;
        t = window.setTimeout(tick, delay);
      };
      tick();
      return () => clearTimeout(t);
    }

    // reveal
    const t = window.setTimeout(() => {
      const next = idx + 1;
      setIdx(next);
      if (next >= plan.length) {
        setStage("done");
      } else {
        setPhase(next % groups === 0 ? "potIntro" : "spin");
      }
    }, REVEAL_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, phase, idx]);

  const confetti = useMemo(
    () =>
      Array.from({ length: 90 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 2.5,
        duration: 3 + Math.random() * 3,
        size: 6 + Math.random() * 8,
        color: ["#FFD54A", "#5AB0FF", "#FF6B8B", "#7CFFB2", "#FFFFFF", "#C792FF"][i % 6],
      })),
    [],
  );

  const sparks = useMemo(() => Array.from({ length: 18 }, (_, i) => i * 20), []);

  const flagOf = (t: DrawShowTeam) => getFlag(t.flag);
  const shownTeam = phase === "reveal" ? current?.team : spinTeam;

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100%",
        color: "#fff",
        overflow: "hidden",
        background: "linear-gradient(120deg, #070b1d, #1b1148, #0a2a4a, #1b1148, #070b1d)",
        backgroundSize: "300% 300%",
        animation: "gd-bg 22s ease infinite",
        px: { xs: 2, md: 5 },
        py: { xs: 2, md: 3 },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{css}</style>

      {/* top bar */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Box>
          <Typography sx={{ letterSpacing: 6, fontSize: 12, color: "rgba(255,255,255,.6)" }}>
            OFFICIAL GROUP STAGE DRAW
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: 20, md: 28 }, letterSpacing: 1 }}>{title}</Typography>
        </Box>
        <Box>
          {stage === "running" && (
            <Button size="small" color="inherit" startIcon={<SkipNextIcon />} onClick={skip} sx={{ opacity: 0.7 }}>
              Skip
            </Button>
          )}
          <IconButton color="inherit" onClick={onClose} disabled={stage === "running" || confirming}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* pot tracker */}
      <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap", my: 1 }}>
        {Array.from({ length: potCount }, (_, p) => {
          const active = stage === "running" && p === potIdx;
          const complete = stage === "done" || idx >= (p + 1) * groups;
          return (
            <Box
              key={p}
              sx={{
                px: 2,
                py: 0.5,
                borderRadius: 99,
                border: `1px solid ${POT_COLORS[p]}`,
                color: active ? "#0b1020" : POT_COLORS[p],
                bgcolor: active ? POT_COLORS[p] : complete ? "rgba(255,255,255,.08)" : "transparent",
                opacity: active || complete ? 1 : 0.45,
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: 2,
                transition: "all .5s",
              }}
            >
              POT {p + 1} {complete && !active ? "✓" : ""}
            </Box>
          );
        })}
      </Box>

      {/* stage */}
      <Box
        sx={{
          height: { xs: 280, md: 300 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        {stage === "intro" && (
          <Box sx={{ textAlign: "center", animation: "gd-rise .9s ease-out" }}>
            <Typography
              sx={{
                fontSize: { xs: 34, md: 64 },
                fontWeight: 900,
                letterSpacing: 6,
                animation: "gd-title 3s ease-in-out infinite",
              }}
            >
              THE DRAW
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,.7)", letterSpacing: 4, mb: 3 }}>
              {plan.length} NATIONS · {groups} GROUPS
            </Typography>
            <Button
              onClick={begin}
              sx={{
                px: 5,
                py: 1.5,
                fontWeight: 800,
                fontSize: 18,
                letterSpacing: 3,
                color: "#0b1020",
                bgcolor: "#FFD54A",
                borderRadius: 99,
                animation: "gd-pulse 2s ease-in-out infinite",
                "&:hover": { bgcolor: "#ffe07a" },
              }}
            >
              BEGIN THE DRAW
            </Button>
          </Box>
        )}

        {stage === "running" && phase === "potIntro" && (
          <Box key={`pot-${potIdx}`} sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                fontSize: { xs: 56, md: 110 },
                fontWeight: 900,
                color: potColor,
                animation: "gd-potin 1.4s cubic-bezier(.2,.8,.2,1) both",
                textShadow: `0 0 50px ${potColor}`,
              }}
            >
              POT {potIdx + 1}
            </Typography>
            <Typography sx={{ letterSpacing: 6, opacity: 0.8, animation: "gd-rise 1.2s .5s both" }}>
              {POT_TAGLINES[potIdx % POT_TAGLINES.length].toUpperCase()}
            </Typography>
          </Box>
        )}

        {stage === "running" && phase !== "potIntro" && shownTeam && current && (
          <Box sx={{ textAlign: "center", position: "relative" }}>
            <Typography sx={{ letterSpacing: 5, fontSize: 13, mb: 1.5, color: potColor }}>
              {phase === "spin"
                ? `DRAWING INTO GROUP ${groupLetters[current.groupIdx]}…`
                : `GROUP ${groupLetters[current.groupIdx]}`}
            </Typography>

            <Box sx={{ position: "relative", display: "inline-block" }}>
              {phase === "spin" && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: -10,
                    borderRadius: 4,
                    border: `2px solid ${potColor}`,
                    boxShadow: `0 0 28px ${potColor}, inset 0 0 18px ${potColor}`,
                    animation: "gd-ring-pulse .7s ease-in-out infinite",
                    pointerEvents: "none",
                  }}
                />
              )}
              {phase === "reveal" && (
                <>
                  <Box
                    sx={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      width: 160,
                      height: 160,
                      ml: "-80px",
                      mt: "-80px",
                      borderRadius: "50%",
                      border: `3px solid ${potColor}`,
                      animation: "gd-shock 1.1s ease-out forwards",
                    }}
                  />
                  {sparks.map((a) => (
                    <Box
                      key={`${idx}-${a}`}
                      sx={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: 4,
                        height: 22,
                        borderRadius: 2,
                        bgcolor: potColor,
                        ["--a" as any]: `${a}deg`,
                        animation: "gd-spark 1s ease-out forwards",
                      }}
                    />
                  ))}
                </>
              )}
              <Box
                key={phase === "reveal" ? `reveal-${idx}` : "spin"}
                sx={{
                  position: "relative",
                  minWidth: { xs: 260, md: 420 },
                  px: 4,
                  py: 2.5,
                  borderRadius: 3,
                  bgcolor: "rgba(10,14,34,.92)",
                  border: `2px solid ${potColor}`,
                  boxShadow: `0 0 ${phase === "reveal" ? 60 : 24}px ${potColor}66`,
                  animation: phase === "reveal" ? "gd-pop .7s cubic-bezier(.2,.9,.3,1.3) both" : "gd-shake .12s linear infinite",
                }}
              >
                <Typography
                  key={shownTeam.id + phase}
                  sx={{
                    fontSize: { xs: 52, md: 84 },
                    lineHeight: 1.1,
                    animation: phase === "spin" ? "gd-flicker .1s linear" : undefined,
                  }}
                >
                  {flagOf(shownTeam)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 24, md: 36 },
                    fontWeight: 900,
                    letterSpacing: 2,
                    filter: phase === "spin" ? "blur(1.2px)" : "none",
                    transition: "filter .3s",
                  }}
                >
                  {shownTeam.name.toUpperCase()}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {stage === "done" && (
          <Box sx={{ textAlign: "center", animation: "gd-rise .8s ease-out" }}>
            <Typography sx={{ fontSize: { xs: 28, md: 52 }, fontWeight: 900, letterSpacing: 5, animation: "gd-title 3s ease-in-out infinite" }}>
              THE GROUPS ARE SET
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap", mt: 3 }}>
              <Button
                variant="contained"
                onClick={onConfirm}
                disabled={confirming}
                sx={{ bgcolor: "#FFD54A", color: "#0b1020", fontWeight: 800, "&:hover": { bgcolor: "#ffe07a" } }}
              >
                {confirming ? "Generating..." : "Confirm & generate matches"}
              </Button>
              <Button color="inherit" variant="outlined" onClick={onRedraw} disabled={confirming}>
                Redraw
              </Button>
              <Button color="inherit" onClick={onBack} disabled={confirming}>
                Back to pots
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* groups */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: `repeat(${groups}, 1fr)` },
          gap: 2.5,
          mt: 2,
          maxWidth: 1300,
          width: "100%",
          mx: "auto",
        }}
      >
        {groupLetters.map((letter, g) => {
          const targeted = stage === "running" && phase !== "potIntro" && current?.groupIdx === g;
          return (
            <Box
              key={letter}
              sx={{
                borderRadius: 3,
                p: 2,
                bgcolor: "rgba(255,255,255,.05)",
                border: "1px solid",
                borderColor: targeted ? potColor : "rgba(255,255,255,.12)",
                backdropFilter: "blur(6px)",
                transition: "border-color .4s",
                animation: targeted ? "gd-glow 1.2s ease-in-out infinite" : undefined,
              }}
            >
              <Typography sx={{ fontWeight: 900, letterSpacing: 5, mb: 1.5, color: targeted ? potColor : "#fff" }}>
                GROUP {letter}
              </Typography>
              {Array.from({ length: potCount }, (_, p) => {
                const pick = plan[p * groups + g];
                const filled = !!pick && p * groups + g < idx;
                return filled ? (
                  <Box
                    key={`${p}-${pick.team.id}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      mb: 1,
                      borderRadius: 2,
                      bgcolor: "rgba(255,255,255,.1)",
                      borderLeft: `5px solid ${POT_COLORS[p % POT_COLORS.length]}`,
                      animation: "gd-slam .6s ease-out both",
                      opacity: pick.team.isBye ? 0.7 : 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 26 }}>{flagOf(pick.team)}</Typography>
                    <Typography sx={{ fontWeight: 700, flex: 1 }}>{pick.team.name}</Typography>
                    <Typography sx={{ fontSize: 11, letterSpacing: 2, color: POT_COLORS[p % POT_COLORS.length] }}>
                      POT {p + 1}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    key={`${p}-empty`}
                    sx={{
                      px: 1.5,
                      py: 1.4,
                      mb: 1,
                      borderRadius: 2,
                      border: "1px dashed rgba(255,255,255,.2)",
                      color: "rgba(255,255,255,.25)",
                      textAlign: "center",
                      letterSpacing: 4,
                      fontWeight: 700,
                    }}
                  >
                    ?
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>

      {stage === "done" &&
        confetti.map((c, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: 0,
              left: `${c.left}%`,
              width: c.size,
              height: c.size * 1.6,
              bgcolor: c.color,
              borderRadius: 0.5,
              pointerEvents: "none",
              animation: `gd-confetti ${c.duration}s ${c.delay}s linear both`,
            }}
          />
        ))}
    </Box>
  );
};

export default GroupDrawShow;
